import "@shoelace-style/shoelace/dist/components/switch/switch.js";
import "@shoelace-style/shoelace/dist/components/details/details.js";
import "@shoelace-style/shoelace/dist/components/checkbox/checkbox.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";

import { html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { StandardFields, AdvancedFields } from "./FilterEditor.form";
import { Form } from "../form/Form";
import { choose } from "lit/directives/choose.js";

import "../FixedPanel/FixedPanel";
import "../DataTableSelect";
import "../ConfirmDialog";
import "../EntryDialog";

import style from "./FilterEditor.style";
import {
    CUSTOM_FILTER_ID,
    IPlaceFilter,
    PlaceFilterTable,
    clonePlaceFilter,
    isCustomFilter,
    makeDefaultPlaceFilter,
} from "../../backend/cloud/PlaceFilterTable";
import { litIfUserInGroup } from "../LitUtils";
import isEqual from "lodash-es/isEqual.js";
import cloneDeep from "lodash-es/cloneDeep.js";
import { PlaceTable } from "../../backend/cloud/PlaceTable";
import { CompiledFilter, compileFilters, compiledFilterPlaceAsync } from "../../Filter";

import { litIf } from "../LitUtils";
import { ChangeSet, ChangeSetListener } from "../../DataStore/ChangeSet";
import { DataProvider } from "../../DataStore/DataProvider";
import { IPlace } from "../../../main";
import { eachLimit } from "async";
import { watch } from "../internal/watch";

@customElement("ds-filter-editor")
export class FilterEditor extends Form implements ChangeSetListener {
    static override styles = [style];

    @property({ type: Boolean }) hideCloseButton = false;
    @property({ type: Boolean }) autoShow = false;
    @property({ type: Object }) filter?: IPlaceFilter;
    @property({ type: Boolean }) noHeader = false;
    @property({ type: String }) buttonSize: "small" | "medium" | "large" = "medium";

    @state() protected _numPlaces = 0;
    @state() protected _editFilterResultsCount = 0;
    @state() protected _filterChanged = false;
    @state() protected _newItem: IPlaceFilter | undefined;
    @state() protected _newItemValid = false;
    @state() protected _saveMode: "save-as" | "save" | "save-public" = "save";
    @state() protected _saveEnabled = false;
    @state() protected _filterName = "";
    @state() protected _signedIn: boolean;
    @state() protected _saving = false;
    @state() protected _allFilters: IPlaceFilter[] = [];
    @state() protected _deleteEnabled = false;

    private _original?: IPlaceFilter;
    private _compiledFilter?: CompiledFilter;
    private _placesTable = this._kernel.dataStore.getSource<PlaceTable>("places");
    private _placeFilters = this._kernel.dataStore.getSource<PlaceFilterTable>("place-filters");

    constructor() {
        super("ds-filter-changed");
        this._signedIn = !!this._kernel.user;
        this.visibleFields = [this._kernel.collection];
    }

    @watch("filter")
    _onFilterChanged() {
        this._setEditFilter(this.filter as IPlaceFilter);
        this._raiseFormChanged();
    }

    async onChangeSet(provider: DataProvider, changeSet: ChangeSet<IPlace>): Promise<void> {
        if (!this._compiledFilter) {
            return;
        }
        const keep = async (p: IPlace) =>
            !(await compiledFilterPlaceAsync(this._compiledFilter!, p));

        const remove = async (p: IPlace) => {
            if (await keep(p)) {
                this._editFilterResultsCount--;
            }
            this._numPlaces--;
        };

        const add = async (p: IPlace) => {
            if (await keep(p)) {
                this._editFilterResultsCount++;
            }
            this._numPlaces++;
        };

        if (provider.name === "places") {
            const toAdd = [];

            for (const change of changeSet.updated) {
                await remove(change.oldValue);
                toAdd.push(change.newValue);
            }
            for (const change of changeSet.deleted) {
                await remove(change);
            }
            for (const change of changeSet.created) {
                toAdd.push(change);
            }
            await eachLimit(toAdd, 10, async (change) => {
                await add(change);
            });
        }
    }

    private _raiseFilterChangedEvent() {
        this._raiseEvent(this._changedEventName, {
            filter: cloneDeep(this.filter),
        });
    }
    protected override _raiseFormChanged() {
        if (this.filter) {
            this._filterChanged = !isEqual(this.filter, this._original);

            this._updateEditFilter();
            this._updateSaveMode();

            if (this.autoShow) {
                this._show();
            }
            this._raiseFilterChangedEvent();
        }
    }

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._subscribeToKernelEvent("filter-changed", (filter: IPlaceFilter) => {
            if (!isEqual(this.filter, filter)) {
                this._setEditFilter(filter);
            }
        });
        this.addEventListener("ds-item-changed", (e: Event) => {
            if ((e as CustomEvent).detail?.property === "country") {
                this._object.state = undefined;
            }
        });

        this._subscribeToKernelEvent("filters-table-changed", (filters: IPlaceFilter[]) => {
            this._allFilters = filters;
        });
        this._allFilters = Array.from(await this._placeFilters.all());
        await this._setEditFilter(this._kernel.currentPlaceFilter.get());
    }

    private _updateSaveMode() {
        this._saveEnabled = false;
        this._deleteEnabled = false;

        if (!this._signedIn || this._saving) {
            return;
        }

        if (this.filter) {
            const isPublic = this.filter.table === "public-filters";
            const isEditor = this._kernel.user?.inGroup("editors");
            if (isPublic) {
                if (isEditor) {
                    this._saveEnabled = this._filterChanged;
                    this._deleteEnabled = !isCustomFilter(this.filter.id as string);
                    this._saveMode = isEditor ? "save-public" : "save-as";
                }
            } else {
                this._saveEnabled = this._filterChanged;
                this._deleteEnabled = !isCustomFilter(this.filter.id as string);
                this._saveMode = "save";
            }
        }
    }

    private async _setEditFilter(filter: IPlaceFilter) {
        this.filter = clonePlaceFilter(filter);
        this._original = clonePlaceFilter(filter);
        this._filterChanged = false;
        this._object = this.filter.filter;
        this._filterName = this.filter.name;
        await this._updateEditFilter();
    }

    private async _updateEditFilter() {
        if (this.filter) {
            this._compiledFilter = compileFilters(this.filter.filter);
            this._updateSaveMode();

            // when we are auto showing results the show button is hidden so we don't need to track the count
            if (!this.autoShow) {
                // Reattach the listener. This will trigger a onChangeSet call with all the current places
                // and watch for changes against the places table.
                this._editFilterResultsCount = 0;
                this._numPlaces = 0;
                this._placesTable.removeListener(this);
                this._placesTable.addListener(this, []);
            }
        }
    }

    private _resetFilter() {
        if (this._original) {
            this._setEditFilter(this._original);
        }
    }

    private async _saveCurrent() {
        if (this.filter) {
            this._saving = true;
            this._saveEnabled = false;
            if (this._saveMode === "save-as" || !this.filter.id || isCustomFilter(this.filter.id)) {
                this.filter.id = undefined;
                this.filter.table = "user-filters";
            }

            if (!this.filter.name) {
                this.filter.name = "Unnamed filter";
            }

            if (!this.filter.id) {
                this.filter = await this._placeFilters.create(this.filter);
            } else {
                this.filter = await this._placeFilters.update(this.filter);
            }
            if (this.filter) {
                this._setEditFilter(this.filter);
            }
            this._saving = false;
        }
    }

    private _deleteCurrent() {
        this._openDialog("delete-filter");
    }

    private _show() {
        if (this.filter) {
            this._kernel.currentPlaceFilter.set(this.filter);
            if (this._filterChanged) {
                this._kernel.events.raiseEvent("filter-changed", this.filter);
            }
            this._raiseEvent("ds-close");
        }
    }

    private _addNewFilter() {
        this._newItem = makeDefaultPlaceFilter();
        (this._newItem as any).name = undefined;
        this._openDialog("add-filter");
    }

    private _setDefaultFilter() {
        this._setEditFilter(Object.assign(makeDefaultPlaceFilter(), { id: CUSTOM_FILTER_ID }));
        this._raiseFormChanged();
    }

    private _renderDeleteConfirmDialog() {
        return html`<ds-confirm-dialog
            open
            .prompt=${`Delete filter?'`}
            .message=${`This will irretrievably delete the filter "${this.filter?.name}". Press 'Yes' to delete or 'Cancel' to abort.`}
            @on-approved="${async () => {
                if (this.filter) {
                    await this._placeFilters.delete(this.filter.id as string, this.filter.table);
                    this._setDefaultFilter();
                }
            }}"
            @on-cancel=${() => {
                this._currentDialog = undefined;
            }}
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        ></ds-confirm-dialog>`;
    }

    private _renderAddFilterDialog() {
        const validateNewItem = () => {
            this._newItemValid =
                !!this._newItem && !!this._newItem.name && this._newItem.name.length > 0;
        };
        return html`<ds-entry-dialog
            open
            label="Create new filter"
            acceptText="Create"
            ?isValid=${this._newItemValid}
            @on-accept=${() => {
                if (this._newItem) {
                    this._setEditFilter(this._newItem);
                    this._newItem = undefined;
                }
            }}
            @on-cancel=${() => {
                this._newItem = undefined;
            }}
            @sl-after-hide=${(e: Event) => this._dialogAfterHide(e)}
        >
            <div class="flex-column">
                <sl-input
                    placeholder="Enter filter name"
                    .value=${this._newItem?.name ?? ""}
                    @sl-input=${(e: any) => {
                        if (this._newItem) {
                            this._newItem.name = e.target.value;
                            validateNewItem();
                        }
                    }}
                ></sl-input>
                ${litIfUserInGroup(
                    "editors",
                    () => html`
                        <sl-switch
                            ?checked=${this._newItem?.table === "public-filters"}
                            @sl-change=${(e: any) => {
                                if (this._newItem) {
                                    this._newItem.table = e.target.checked
                                        ? "public-filters"
                                        : "user-filters";
                                    validateNewItem();
                                }
                            }}
                            >Public</sl-switch
                        >
                    `
                )}
            </div>
        </ds-entry-dialog>`;
    }

    override render() {
        return html` <!-- Dialogs -->
            ${choose(this._currentDialog, [
                ["delete-filter", () => this._renderDeleteConfirmDialog()],
                ["add-filter", () => this._renderAddFilterDialog()],
            ])}

            <!-- Main panel -->
            <ds-fixed-panel
                >${litIf(
                    !this.noHeader,
                    () =>
                        html`<div class="flex-row reset-button" slot="header">
                            <div class="flex-row width_100pc">
                                <sl-dropdown>
                                    <sl-button
                                        class="width_100pc"
                                        pill
                                        size="medium"
                                        slot="trigger"
                                        caret
                                        >Choose filter</sl-button
                                    >
                                    <sl-menu>
                                        ${this._allFilters.map(
                                            (x) =>
                                                html`<sl-menu-item
                                                    @click=${async () => {
                                                        await this._setEditFilter(x);
                                                        if (this.autoShow) {
                                                            this._show();
                                                        }
                                                    }}
                                                    >${x.name}
                                                    <sl-icon
                                                        slot="prefix"
                                                        name=${x.table === "public-filters"
                                                            ? "shield-shaded"
                                                            : "person-fill"}
                                                    ></sl-icon>
                                                </sl-menu-item>`
                                        )}
                                    </sl-menu></sl-dropdown
                                >
                            </div>
                            <a @click=${() => this._setDefaultFilter()}>Reset</a>
                        </div>`
                )}
                <div class="body" slot="body">
                    <span class="editor-label">Filter name</span>
                    <sl-input
                        pill
                        filled
                        size="small"
                        placeholder="Enter filter name"
                        .value=${this._filterName}
                        @sl-input=${(e: any) => {
                            if (this.filter) {
                                this._filterName = this.filter.name = e.target.value;

                                this._raiseFormChanged();
                            }
                        }}
                    ></sl-input>
                    ${this._renderForm(StandardFields)}
                    <sl-details summary="More filters" class="advanced">
                        ${this._renderForm(AdvancedFields)}
                    </sl-details>
                </div>
                <div class="footer flex-row" slot="footer">
                    ${litIf(
                        this._filterChanged,
                        () =>
                            html` <sl-button
                                pill
                                .size=${this.buttonSize}
                                ?disabled=${!this._filterChanged}
                                variant="default"
                                @click=${() => this._resetFilter()}
                                >Undo changes</sl-button
                            >`
                    )}
                    ${litIf(
                        !this.hideCloseButton,
                        () =>
                            html` <sl-button
                                pill
                                .size=${this.buttonSize}
                                variant="default"
                                @click=${() => this._raiseEvent("ds-close")}
                                >Close</sl-button
                            >`
                    )}
                    ${litIf(
                        this._saveEnabled,
                        () =>
                            html` <sl-tooltip content="Save the current filter">
                                <sl-button
                                    pill
                                    .size=${this.buttonSize}
                                    variant="default"
                                    @click=${() => this._saveCurrent()}
                                    ?disabled=${!this._saveEnabled}
                                    >${choose(this._saveMode, [
                                        ["save-as", () => html`Save as`],
                                        ["save", () => html`Save`],
                                        ["save-public", () => html`Save public`],
                                    ])}</sl-button
                                >
                            </sl-tooltip>`
                    )}
                    ${litIf(
                        this._deleteEnabled,
                        () =>
                            html` <sl-button
                                pill
                                .size=${this.buttonSize}
                                variant="default"
                                @click=${() => this._deleteCurrent()}
                                >${this._original?.table === "public-filters"
                                    ? "Delete public"
                                    : "Delete"}</sl-button
                            >`
                    )}
                    ${litIf(
                        !this.autoShow,
                        () =>
                            html` <sl-button
                                pill
                                .size=${this.buttonSize}
                                variant="default"
                                ?disabled=${this._editFilterResultsCount === 0}
                                @click=${() => this._show()}
                                >Show ${this._editFilterResultsCount} of ${this._numPlaces}
                                places</sl-button
                            >`
                    )}
                </div>
            </ds-fixed-panel>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-filter-editor": FilterEditor;
    }
}
